import { useEffect, useRef } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY?.trim();
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

// Minimal-Typen für das global eingebundene Turnstile-Objekt.
declare global {
    interface Window {
        turnstile?: {
            render: (el: HTMLElement, opts: Record<string, unknown>) => string;
            reset: (id?: string) => void;
            remove: (id?: string) => void;
        };
    }
}

function loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (window.turnstile) return resolve();
        const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
        if (existing) {
            existing.addEventListener("load", () => resolve());
            existing.addEventListener("error", () => reject(new Error("Turnstile konnte nicht geladen werden.")));
            return;
        }
        const script = document.createElement("script");
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Turnstile konnte nicht geladen werden."));
        document.head.appendChild(script);
    });
}

type Props = {
    onToken: (token: string) => void;   // liefert das fertige Token
    onExpire?: () => void;              // Token abgelaufen / Fehler -> Aufrufer sperrt wieder
    resetSignal?: number;              // ändert sich -> Widget neu (frisches Token)
};

function Turnstile({ onToken, onExpire, resetSignal }: Props) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);

    // Callbacks in Refs halten, damit das Widget nur EINMAL gerendert wird
    // und trotzdem immer die aktuellen Handler benutzt.
    const onTokenRef = useRef(onToken);
    const onExpireRef = useRef(onExpire);
    onTokenRef.current = onToken;
    onExpireRef.current = onExpire;

    useEffect(() => {
        if (!SITE_KEY) {
            onExpireRef.current?.();
            return;
        }

        let cancelled = false;
        loadScript()
            .then(() => {
                if (cancelled || !containerRef.current || !window.turnstile) return;
                widgetIdRef.current = window.turnstile.render(containerRef.current, {
                    sitekey: SITE_KEY,
                    callback: (token: string) => onTokenRef.current(token),
                    "expired-callback": () => onExpireRef.current?.(),
                    "error-callback": () => onExpireRef.current?.(),
                });
            })
            .catch(() => {
                // Bleibt still: ohne Token bleibt der Absenden-Button gesperrt.
            });

        return () => {
            cancelled = true;
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, []);

    // Nach einem fehlgeschlagenen Absenden: frisches Token holen
    // (wichtig für echte Keys – Tokens sind einmalig).
    useEffect(() => {
        if (resetSignal === undefined) return;
        if (widgetIdRef.current && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
        }
    }, [resetSignal]);

    if (!SITE_KEY) {
        return <div className="turnstile-widget" />;
    }

    return <div ref={containerRef} className="turnstile-widget" />;
}

export default Turnstile;
